import type {
  HighlightOptions,
  HighlightInfo,
  HighlightConfig
} from '../types/highlighter';
import { EventBus } from '../core/event-bus';
import { Logger } from '../core/logger';
import {
  generateHighlightStyles,
  generateLabelStyles,
  injectHighlighterAnimations,
  removeHighlighterAnimations
} from '../utils/highlighter-styles';
import { getElementPosition } from '../utils/dom-utils';

export class UIHighlighter {
  private config: HighlightConfig;
  private highlights: Map<string, HighlightInfo>;
  private eventBus: EventBus;
  private logger: Logger;
  private observer: ResizeObserver | null;
  private updateScheduled: boolean;

  constructor(
    eventBus: EventBus,
    logger: Logger,
    defaultColor: string = '#FFD700'
  ) {
    this.eventBus = eventBus;
    this.logger = logger;
    this.highlights = new Map();
    this.updateScheduled = false;

    this.config = {
      defaultColor,
      defaultDuration: 3000,
      defaultStyle: 'solid',
      defaultZIndex: 999999,
      animationDuration: 300
    };

    // Initialize ResizeObserver to update highlights on element resize
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => {
        this.scheduleUpdate();
      });
    } else {
      this.observer = null;
      this.logger.warn('ResizeObserver not available, highlights may not update on resize');
    }

    // Inject CSS animations
    injectHighlighterAnimations();

    this.logger.debug('UIHighlighter initialized');
  }

  /**
   * Highlight an element
   */
  highlight(
    element: HTMLElement | string,
    options: HighlightOptions = {}
  ): string {
    const targetElement = typeof element === 'string'
      ? document.querySelector<HTMLElement>(element)
      : element;

    if (!targetElement) {
      this.logger.error('Element not found for highlighting');
      throw new Error('Element not found');
    }

    // Generate unique ID
    const id = this.generateId();

    // Merge with defaults
    const fullOptions: Required<HighlightOptions> = {
      color: options.color || this.config.defaultColor,
      duration: options.duration ?? this.config.defaultDuration,
      label: options.label || '',
      style: options.style || this.config.defaultStyle,
      zIndex: options.zIndex ?? this.config.defaultZIndex
    };

    // Create overlay element
    const overlay = this.createOverlay(targetElement, fullOptions);

    // Store highlight info
    const highlightInfo: HighlightInfo = {
      id,
      element: targetElement,
      overlay,
      options: fullOptions,
      createdAt: Date.now()
    };

    this.highlights.set(id, highlightInfo);

    // Observe element for resize
    if (this.observer) {
      this.observer.observe(targetElement);
    }

    // Auto-remove after duration
    if (fullOptions.duration > 0) {
      setTimeout(() => {
        this.removeHighlight(id);
      }, fullOptions.duration);
    }

    // Emit event
    this.eventBus.emit('highlight:added', { id, element: targetElement, options: fullOptions });
    this.logger.debug(`Highlight added: ${id}`);

    return id;
  }

  /**
   * Remove a highlight by ID
   */
  removeHighlight(id: string): boolean {
    const highlight = this.highlights.get(id);
    if (!highlight) {
      return false;
    }

    // Stop observing element
    if (this.observer) {
      this.observer.unobserve(highlight.element);
    }

    // Fade out animation
    highlight.overlay.style.opacity = '0';

    setTimeout(() => {
      if (highlight.overlay.parentNode) {
        highlight.overlay.parentNode.removeChild(highlight.overlay);
      }
      this.highlights.delete(id);
      this.eventBus.emit('highlight:removed', { id });
      this.logger.debug(`Highlight removed: ${id}`);
    }, this.config.animationDuration);

    return true;
  }

  /**
   * Remove all highlights
   */
  clearAll(): void {
    const ids = Array.from(this.highlights.keys());
    ids.forEach(id => this.removeHighlight(id));
    this.logger.debug('All highlights cleared');
  }

  /**
   * Update highlight positions (e.g., after scroll or resize)
   */
  updatePositions(): void {
    this.highlights.forEach(highlight => {
      this.updateOverlayPosition(highlight.overlay, highlight.element);
    });
  }

  /**
   * Get all active highlights
   */
  getActiveHighlights(): HighlightInfo[] {
    return Array.from(this.highlights.values());
  }

  /**
   * Cleanup and remove all highlights
   */
  destroy(): void {
    this.clearAll();

    if (this.observer) {
      this.observer.disconnect();
    }

    removeHighlighterAnimations();
    this.logger.debug('UIHighlighter destroyed');
  }

  /**
   * Create overlay element for highlighting
   */
  private createOverlay(
    element: HTMLElement,
    options: Required<HighlightOptions>
  ): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = 'eazy-navigator-highlight';
    overlay.setAttribute('data-eazy-highlight', 'true');

    // Apply styles
    const styles = generateHighlightStyles(
      options.color,
      options.style,
      this.config.animationDuration
    );
    overlay.style.cssText = styles;
    overlay.style.zIndex = String(options.zIndex);

    // Set position
    this.updateOverlayPosition(overlay, element);

    // Add label if provided
    if (options.label) {
      const label = document.createElement('div');
      label.className = 'eazy-navigator-highlight-label';
      label.textContent = options.label;
      label.style.cssText = generateLabelStyles(options.color);
      overlay.appendChild(label);
    }

    // Append to body
    document.body.appendChild(overlay);

    return overlay;
  }

  /**
   * Update overlay position to match element
   */
  private updateOverlayPosition(overlay: HTMLElement, element: HTMLElement): void {
    const rect = getElementPosition(element);

    overlay.style.top = `${rect.y}px`;
    overlay.style.left = `${rect.x}px`;
    overlay.style.width = `${rect.width}px`;
    overlay.style.height = `${rect.height}px`;
  }

  /**
   * Schedule position update (debounced)
   */
  private scheduleUpdate(): void {
    if (this.updateScheduled) {
      return;
    }

    this.updateScheduled = true;
    requestAnimationFrame(() => {
      this.updatePositions();
      this.updateScheduled = false;
    });
  }

  /**
   * Generate unique ID for highlight
   */
  private generateId(): string {
    return `highlight-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
